import type {
  ComparableAggregate,
  ComparableCoolingObservationValue,
  ComparableDeliveryEventValue,
  ComparablePowerObservationValue,
  ComparablePowerTechnology,
  ComparableMatchResult,
  ComparableProjectRecord,
  ComparableWaterObservationValue,
  EconomicEvidenceBasis,
  MwQuantity,
  PowerMwBasis,
} from '../../types/economicContribution';
import { roundEconomic } from './modelUtils';

export interface ComparableTarget {
  load: MwQuantity;
  operatingModel: ComparableProjectRecord['operatingModel'];
  workload: ComparableProjectRecord['workload'];
  stateFips: string;
}

export type ComparableTopic = 'jobs' | 'water' | 'cooling' | 'power' | 'delivery';

export interface ComparableMwEnvelope {
  lowMw: number;
  highMw: number;
  basis: PowerMwBasis;
  phaseBasis?: string;
}

export interface ComparableTopicTarget {
  /** Omitted when MW is unknown; discovery and non-normalized matching still proceed. */
  load?: ComparableMwEnvelope;
  operatingModel: ComparableProjectRecord['operatingModel'];
  workload: ComparableProjectRecord['workload'];
  stateFips?: string;
  waterSource?: ComparableWaterObservationValue['waterSource'];
  cooling?: Partial<Pick<
    ComparableCoolingObservationValue,
    'rackCoolingStrategy' | 'heatRejection' | 'coolingGeneration'
  >>;
  powerTechnologies?: readonly ComparablePowerTechnology[];
}

export interface ComparableTopicMatchResult {
  topic: ComparableTopic;
  record: ComparableProjectRecord;
  /** Deterministic relevance score, bounded to 0–100. */
  score: number;
  /** Null means MW was unknown or incomparable; it is not a zero score. */
  mwOverlap: number | null;
  reasons: readonly string[];
  incompatibilities: readonly string[];
  compatible: boolean;
}

export interface ComparableTopicNumericAggregate {
  key: string;
  recordCount: number;
  low: number;
  median: number;
  high: number;
  units: string;
}

export interface ComparableTopicCategoricalAggregate {
  key: string;
  recordCount: number;
}

export interface ComparableTopicAggregateResult {
  topic: ComparableTopic;
  compatibleRecordCount: number;
  method: 'topic-compatible-envelope' | 'topic-compatible-frequency' | null;
  numeric: readonly ComparableTopicNumericAggregate[];
  categorical: readonly ComparableTopicCategoricalAggregate[];
}

export const MIN_COMPARABLE_AGGREGATE_RECORDS = 3;

const EVIDENCE_SCORE: Record<ComparableProjectRecord['confidence'], number> = {
  high: 10,
  medium: 6,
  low: 2,
};

const EVIDENCE_RECENCY_RANK: Record<Exclude<EconomicEvidenceBasis, 'modelled' | 'operator-provided' | 'secondary-discovery'>, number> = {
  'verified-actual': 4,
  'observed-public-record': 3,
  committed: 2,
  announced: 1,
};

function normalizedIdentity(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Exact project-entity identity wins; facility + county is the conservative fallback. */
export function comparableEntityKey(record: ComparableProjectRecord): string {
  return record.projectEntity?.trim()
    ? `entity:${normalizedIdentity(record.projectEntity)}`
    : `facility:${record.location.countyFips}:${normalizedIdentity(record.facilityName)}`;
}

function preferredDuplicate(left: ComparableProjectRecord, right: ComparableProjectRecord): ComparableProjectRecord {
  const jobs = (record: ComparableProjectRecord) => record.permanentJobs ?? record.permanentJobsRange;
  const rank = (record: ComparableProjectRecord): number => {
    const basis = jobs(record)?.evidenceBasis;
    const evidence = basis && basis in EVIDENCE_RECENCY_RANK
      ? EVIDENCE_RECENCY_RANK[basis as keyof typeof EVIDENCE_RECENCY_RANK]
      : 0;
    const confidence = EVIDENCE_SCORE[record.confidence];
    const asOf = Date.parse(jobs(record)?.asOf ?? '') || 0;
    return evidence * 1e16 + confidence * 1e13 + asOf;
  };
  const difference = rank(right) - rank(left);
  if (difference !== 0) return difference > 0 ? right : left;
  return right.projectId.localeCompare(left.projectId) < 0 ? right : left;
}

function deduplicateComparableProjects(records: readonly ComparableProjectRecord[]): ComparableProjectRecord[] {
  const byEntity = new Map<string, ComparableProjectRecord>();
  for (const record of records) {
    const key = comparableEntityKey(record);
    const current = byEntity.get(key);
    byEntity.set(key, current ? preferredDuplicate(current, record) : record);
  }
  return [...byEntity.values()];
}

function permanentJobsObservation(record: ComparableProjectRecord) {
  return record.permanentJobs ?? record.permanentJobsRange ?? null;
}

function phaseCompatible(record: ComparableProjectRecord): boolean {
  const jobs = permanentJobsObservation(record);
  return Boolean(record.mw && jobs && record.mw.phaseBasis === jobs.phaseBasis);
}

function jobsPerMwCompatibility(record: ComparableProjectRecord, target: ComparableTarget): string[] {
  const issues: string[] = [];
  if (!record.mw) issues.push('MW observation unavailable.');
  if (!permanentJobsObservation(record)) issues.push('Permanent-jobs observation unavailable.');
  if (record.mw && record.mw.value.basis !== target.load.basis) issues.push('MW basis differs from the subject site.');
  if (record.mw && record.mw.value.value <= 0) issues.push('MW denominator is not positive.');
  if (!phaseCompatible(record)) issues.push('Jobs and MW observations do not share a phase basis.');
  return issues;
}

export function matchComparableProjects(
  target: ComparableTarget,
  records: readonly ComparableProjectRecord[],
): ComparableMatchResult[] {
  return deduplicateComparableProjects(records
    .filter((record) => record.reviewStatus === 'approved' && record.status !== 'cancelled'))
    .map((record) => {
      const reasons: string[] = [];
      let score = EVIDENCE_SCORE[record.confidence];
      if (record.mw && record.mw.value.basis === target.load.basis) {
        const scaleRatio = Math.max(record.mw.value.value, target.load.value)
          / Math.min(record.mw.value.value, target.load.value);
        const scalePoints = Math.max(0, 35 - Math.log2(scaleRatio) * 12);
        score += scalePoints;
        reasons.push(`Load scale match contributes ${roundEconomic(scalePoints, 1)} points.`);
        score += 10;
        reasons.push('MW basis matches.');
      }
      if (record.operatingModel === target.operatingModel && target.operatingModel !== 'unknown') {
        score += 20;
        reasons.push('Operating model matches.');
      }
      if (record.workload === target.workload && target.workload !== 'unknown') {
        score += 10;
        reasons.push('Workload matches.');
      }
      if (record.location.stateFips === target.stateFips) {
        score += 5;
        reasons.push('Same state context.');
      }
      const incompatibilities = jobsPerMwCompatibility(record, target);
      return {
        record,
        score: roundEconomic(score, 1),
        reasons,
        incompatibilities,
        compatibleForJobsPerMw: incompatibilities.length === 0,
      };
    })
    .sort((left, right) => right.score - left.score || left.record.projectId.localeCompare(right.record.projectId));
}

function quantile(values: readonly number[], fraction: number): number {
  if (values.length === 0) throw new RangeError('Cannot calculate a quantile for an empty collection.');
  const sorted = [...values].sort((a, b) => a - b);
  const position = (sorted.length - 1) * fraction;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);
  const lower = sorted[lowerIndex]!;
  const upper = sorted[upperIndex]!;
  return roundEconomic(lower + (upper - lower) * (position - lowerIndex), 2);
}

function range(values: readonly number[]): { low: number; median: number; high: number } {
  return { low: quantile(values, 0), median: quantile(values, 0.5), high: quantile(values, 1) };
}

function observationRange(record: ComparableProjectRecord): { low: number; high: number } | null {
  if (record.permanentJobs) {
    return { low: record.permanentJobs.value, high: record.permanentJobs.value };
  }
  return record.permanentJobsRange?.value ?? null;
}

function intervalEnvelope(
  intervals: readonly { low: number; high: number }[],
): { low: number; median: number; high: number } {
  return {
    low: roundEconomic(Math.min(...intervals.map((interval) => interval.low)), 2),
    median: quantile(intervals.map((interval) => (interval.low + interval.high) / 2), 0.5),
    high: roundEconomic(Math.max(...intervals.map((interval) => interval.high)), 2),
  };
}

type AggregateBasis = ComparableAggregate['evidenceBasis'];
const AGGREGATE_BASES: readonly AggregateBasis[] = [
  'verified-actual',
  'observed-public-record',
  'committed',
  'announced',
];

/** Statistical summaries remain empty until three phase/basis-compatible approved observations exist. */
export function aggregateComparableMatches(matches: readonly ComparableMatchResult[]): ComparableAggregate[] {
  return AGGREGATE_BASES.map((evidenceBasis) => {
    const compatible = matches.filter((match) => (
      match.compatibleForJobsPerMw
      && permanentJobsObservation(match.record)?.evidenceBasis === evidenceBasis
      && match.record.mw != null
    ));
    if (compatible.length < 3) {
      return {
        evidenceBasis,
        compatibleRecordCount: compatible.length,
        permanentJobs: null,
        jobsPerMw: null,
        method: null,
      };
    }
    const permanentJobs = compatible.map((match) => observationRange(match.record)!);
    const jobsPerMw = compatible.map((match) => {
      const jobs = observationRange(match.record)!;
      const mw = match.record.mw!.value.value;
      return { low: jobs.low / mw, high: jobs.high / mw };
    });
    return {
      evidenceBasis,
      compatibleRecordCount: compatible.length,
      permanentJobs: intervalEnvelope(permanentJobs),
      jobsPerMw: intervalEnvelope(jobsPerMw),
      method: 'compatible-observation-envelope',
    };
  });
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * Range overlap is always bounded to 0–1. Disjoint ranges decay to zero over
 * three doublings, avoiding unbounded scale bonuses and exact-MW blockers.
 */
export function boundedMwOverlapScore(
  target: Pick<ComparableMwEnvelope, 'lowMw' | 'highMw'>,
  candidate: Pick<ComparableMwEnvelope, 'lowMw' | 'highMw'>,
): number {
  const values = [target.lowMw, target.highMw, candidate.lowMw, candidate.highMw];
  if (values.some((value) => !Number.isFinite(value) || value <= 0)
    || target.highMw < target.lowMw || candidate.highMw < candidate.lowMw) {
    return 0;
  }
  const overlaps = candidate.lowMw <= target.highMw && target.lowMw <= candidate.highMw;
  if (overlaps) {
    const targetWidth = target.highMw - target.lowMw;
    const candidateWidth = candidate.highMw - candidate.lowMw;
    if (targetWidth === 0 || candidateWidth === 0) return 1;
    const intersection = Math.min(target.highMw, candidate.highMw)
      - Math.max(target.lowMw, candidate.lowMw);
    return clamp01(intersection / Math.min(targetWidth, candidateWidth));
  }
  const scaleRatio = candidate.lowMw > target.highMw
    ? candidate.lowMw / target.highMw
    : target.lowMw / candidate.highMw;
  return clamp01(1 - Math.log2(scaleRatio) / 3);
}

function topicObservationCount(record: ComparableProjectRecord, topic: ComparableTopic): number {
  switch (topic) {
    case 'jobs':
      return Number(Boolean(record.permanentJobs ?? record.permanentJobsRange));
    case 'water':
      return (record.waterTimeline?.length ?? 0) + Number(Boolean(record.water));
    case 'cooling':
      return record.coolingObservations?.length ?? 0;
    case 'power':
      return record.powerObservations?.length ?? 0;
    case 'delivery':
      return record.deliveryEvents?.length ?? 0;
  }
}

function latestTopicDate(record: ComparableProjectRecord, topic: ComparableTopic): number {
  const dates: string[] = [];
  if (topic === 'jobs') {
    const jobs = permanentJobsObservation(record);
    if (jobs?.asOf) dates.push(jobs.asOf);
  } else if (topic === 'water') {
    for (const observation of record.waterTimeline ?? []) {
      if (observation.asOf) dates.push(observation.asOf);
      if (observation.value.periodEnd) dates.push(observation.value.periodEnd);
    }
  } else if (topic === 'cooling') {
    for (const observation of record.coolingObservations ?? []) if (observation.asOf) dates.push(observation.asOf);
  } else if (topic === 'power') {
    for (const observation of record.powerObservations ?? []) if (observation.asOf) dates.push(observation.asOf);
  } else {
    for (const observation of record.deliveryEvents ?? []) dates.push(observation.value.occurredOn);
  }
  return Math.max(0, ...dates.map((value) => Date.parse(value)).filter(Number.isFinite));
}

function preferredDuplicateForTopic(
  left: ComparableProjectRecord,
  right: ComparableProjectRecord,
  topic: ComparableTopic,
): ComparableProjectRecord {
  const compare = (selector: (record: ComparableProjectRecord) => number): number => selector(right) - selector(left);
  const byObservationCount = compare((record) => topicObservationCount(record, topic));
  if (byObservationCount !== 0) return byObservationCount > 0 ? right : left;
  const byConfidence = compare((record) => EVIDENCE_SCORE[record.confidence]);
  if (byConfidence !== 0) return byConfidence > 0 ? right : left;
  const byDate = compare((record) => latestTopicDate(record, topic));
  if (byDate !== 0) return byDate > 0 ? right : left;
  return right.projectId.localeCompare(left.projectId) < 0 ? right : left;
}

function deduplicateForTopic(
  records: readonly ComparableProjectRecord[],
  topic: ComparableTopic,
): ComparableProjectRecord[] {
  const byEntity = new Map<string, ComparableProjectRecord>();
  for (const record of records) {
    const key = comparableEntityKey(record);
    const current = byEntity.get(key);
    byEntity.set(key, current ? preferredDuplicateForTopic(current, record, topic) : record);
  }
  return [...byEntity.values()];
}

function addTopicSimilarity(
  topic: ComparableTopic,
  target: ComparableTopicTarget,
  record: ComparableProjectRecord,
  reasons: string[],
  incompatibilities: string[],
): number {
  let score = 0;
  if (topic === 'water' && target.waterSource && target.waterSource !== 'unknown') {
    const matches = (record.waterTimeline ?? []).some((observation) => (
      observation.value.waterSource === target.waterSource
    ));
    if (matches) {
      score += 8;
      reasons.push('Water-source type matches.');
    } else {
      incompatibilities.push('Water-source type is different or unavailable.');
    }
  }
  if (topic === 'cooling' && target.cooling) {
    const axes = [
      ['rackCoolingStrategy', target.cooling.rackCoolingStrategy],
      ['heatRejection', target.cooling.heatRejection],
      ['coolingGeneration', target.cooling.coolingGeneration],
    ] as const;
    for (const [axis, expected] of axes) {
      if (!expected || expected === 'unknown') continue;
      const matches = (record.coolingObservations ?? []).some((observation) => observation.value[axis] === expected);
      if (matches) {
        score += 6;
        reasons.push(`${axis} matches.`);
      } else {
        incompatibilities.push(`${axis} differs or is unavailable.`);
      }
    }
  }
  if (topic === 'power' && target.powerTechnologies?.length) {
    const available = new Set((record.powerObservations ?? []).flatMap((observation) => observation.value.technologies));
    const matching = [...new Set(target.powerTechnologies)].filter((technology) => available.has(technology));
    if (matching.length) {
      const points = Math.min(12, matching.length * 6);
      score += points;
      reasons.push(`${matching.length} power technolog${matching.length === 1 ? 'y matches' : 'ies match'}.`);
    } else {
      incompatibilities.push('Power technologies differ or are unavailable.');
    }
  }
  return score;
}

/** Topic-specific matching; unknown MW lowers precision but never blocks discovery. */
export function matchComparableProjectsByTopic(
  topic: ComparableTopic,
  target: ComparableTopicTarget,
  records: readonly ComparableProjectRecord[],
): ComparableTopicMatchResult[] {
  const eligible = records.filter((record) => (
    record.reviewStatus === 'approved' && (topic === 'delivery' || record.status !== 'cancelled')
  ));
  return deduplicateForTopic(eligible, topic)
    .map((record) => {
      const reasons: string[] = [];
      const incompatibilities: string[] = [];
      const observationCount = topicObservationCount(record, topic);
      let score = EVIDENCE_SCORE[record.confidence] + Math.min(15, observationCount * 5);
      let mwOverlap: number | null = null;
      if (target.load) {
        if (!record.mw) {
          incompatibilities.push('Comparable MW is unavailable; no scale normalization was applied.');
        } else if (record.mw.value.basis !== target.load.basis) {
          incompatibilities.push('Comparable and subject MW bases differ; no scale normalization was applied.');
        } else {
          mwOverlap = boundedMwOverlapScore(target.load, {
            lowMw: record.mw.value.value,
            highMw: record.mw.value.value,
          });
          score += mwOverlap * 35 + 5;
          reasons.push(`Bounded MW overlap contributes ${roundEconomic(mwOverlap * 35, 1)} points.`);
          reasons.push('MW basis matches.');
        }
      } else {
        reasons.push('Subject MW is unknown; topic evidence was matched without scale normalization.');
      }
      if (record.operatingModel === target.operatingModel && target.operatingModel !== 'unknown') {
        score += 15;
        reasons.push('Operating model matches.');
      }
      if (record.workload === target.workload && target.workload !== 'unknown') {
        score += 8;
        reasons.push('Workload matches.');
      }
      if (target.stateFips && record.location.stateFips === target.stateFips) {
        score += 4;
        reasons.push('Same state context.');
      }
      score += addTopicSimilarity(topic, target, record, reasons, incompatibilities);
      if (observationCount === 0) incompatibilities.unshift(`No ${topic} observation is available.`);
      return {
        topic,
        record,
        score: roundEconomic(Math.max(0, Math.min(100, score)), 1),
        mwOverlap,
        reasons,
        incompatibilities,
        compatible: observationCount > 0,
      };
    })
    .sort((left, right) => right.score - left.score || left.record.projectId.localeCompare(right.record.projectId));
}

function addFrequency(
  groups: Map<string, Set<string>>,
  key: string,
  projectId: string,
): void {
  const projects = groups.get(key) ?? new Set<string>();
  projects.add(projectId);
  groups.set(key, projects);
}

function categoricalAggregates(
  groups: ReadonlyMap<string, ReadonlySet<string>>,
): ComparableTopicCategoricalAggregate[] {
  return [...groups.entries()]
    .filter(([, projects]) => projects.size >= MIN_COMPARABLE_AGGREGATE_RECORDS)
    .map(([key, projects]) => ({ key, recordCount: projects.size }))
    .sort((left, right) => right.recordCount - left.recordCount || left.key.localeCompare(right.key));
}

function waterNumericAggregates(
  matches: readonly ComparableTopicMatchResult[],
): ComparableTopicNumericAggregate[] {
  const groups = new Map<string, Map<string, number[]>>();
  for (const match of matches.filter((entry) => entry.compatible)) {
    for (const observation of match.record.waterTimeline ?? []) {
      const normalized = observation.value.normalizedGallonsPerDay;
      if (normalized == null || !Number.isFinite(normalized)
        || observation.value.flow === 'unspecified'
        || observation.value.statistic === 'unspecified') continue;
      const periodStart = observation.value.periodStart ? Date.parse(observation.value.periodStart) : NaN;
      const periodEnd = observation.value.periodEnd ? Date.parse(observation.value.periodEnd) : NaN;
      const periodBasis = Number.isFinite(periodStart) && Number.isFinite(periodEnd)
        ? `${Math.floor((periodEnd - periodStart) / 86_400_000) + 1}-day-reporting-period`
        : 'no-reporting-period';
      const key = [
        observation.value.stage,
        observation.value.flow,
        observation.value.statistic,
        observation.value.boundary,
        `evidence:${observation.evidenceBasis}`,
        `observation-phase:${observation.phaseBasis}`,
        `native:${observation.value.units}`,
        'canonical:gal/day',
        `time:${periodBasis}`,
        `mw-basis:${match.record.mw?.value.basis ?? 'unknown'}`,
        `mw-phase:${match.record.mw?.phaseBasis ?? 'unknown'}`,
      ].join('|');
      const byProject = groups.get(key) ?? new Map<string, number[]>();
      const projectValues = byProject.get(match.record.projectId) ?? [];
      projectValues.push(normalized);
      byProject.set(match.record.projectId, projectValues);
      groups.set(key, byProject);
    }
  }
  const result: ComparableTopicNumericAggregate[] = [];
  for (const [key, byProject] of groups) {
    const unconflicted = [...byProject.values()]
      .filter((values) => new Set(values).size === 1)
      .map((values) => values[0]!);
    if (unconflicted.length < MIN_COMPARABLE_AGGREGATE_RECORDS) continue;
    result.push({
      key,
      recordCount: unconflicted.length,
      ...range(unconflicted),
      units: 'gal/day',
    });
  }
  return result.sort((left, right) => left.key.localeCompare(right.key));
}

function jobsNumericAggregates(
  matches: readonly ComparableTopicMatchResult[],
): ComparableTopicNumericAggregate[] {
  const groups = new Map<string, { projectId: string; low: number; high: number }[]>();
  for (const match of matches.filter((entry) => entry.compatible)) {
    const jobs = permanentJobsObservation(match.record);
    const interval = observationRange(match.record);
    if (!jobs || !interval) continue;
    const values = groups.get(jobs.evidenceBasis) ?? [];
    values.push({ projectId: match.record.projectId, ...interval });
    groups.set(jobs.evidenceBasis, values);
  }
  return [...groups.entries()]
    .filter(([, values]) => new Set(values.map((value) => value.projectId)).size >= MIN_COMPARABLE_AGGREGATE_RECORDS)
    .map(([key, values]) => ({
      key,
      recordCount: new Set(values.map((value) => value.projectId)).size,
      ...intervalEnvelope(values),
      units: 'permanent onsite FTE',
    }))
    .sort((left, right) => left.key.localeCompare(right.key));
}

/** Every aggregate cell requires at least three distinct compatible projects. */
export function aggregateComparableTopicMatches(
  topic: ComparableTopic,
  matches: readonly ComparableTopicMatchResult[],
): ComparableTopicAggregateResult {
  const compatible = matches.filter((match) => match.topic === topic && match.compatible);
  const groups = new Map<string, Set<string>>();
  if (topic === 'cooling') {
    for (const match of compatible) {
      for (const observation of match.record.coolingObservations ?? []) {
        const value = observation.value;
        if (value.rackCoolingStrategy && value.rackCoolingStrategy !== 'unknown') {
          addFrequency(groups, `rack-cooling:${value.rackCoolingStrategy}`, match.record.projectId);
        }
        if (value.heatRejection && value.heatRejection !== 'unknown') {
          addFrequency(groups, `heat-rejection:${value.heatRejection}`, match.record.projectId);
        }
        if (value.coolingGeneration && value.coolingGeneration !== 'unknown') {
          addFrequency(groups, `cooling-generation:${value.coolingGeneration}`, match.record.projectId);
        }
      }
    }
  } else if (topic === 'power') {
    for (const match of compatible) {
      for (const observation of match.record.powerObservations ?? []) {
        for (const technology of observation.value.technologies.filter((value) => value !== 'unknown')) {
          addFrequency(
            groups,
            `${observation.value.role}|${technology}|${observation.value.status}`,
            match.record.projectId,
          );
        }
      }
    }
  } else if (topic === 'delivery') {
    for (const match of compatible) {
      for (const observation of match.record.deliveryEvents ?? []) {
        const value = observation.value;
        addFrequency(groups, `${value.category}|${value.eventType}|${value.effect}`, match.record.projectId);
      }
    }
  }
  const numeric = topic === 'water'
    ? waterNumericAggregates(compatible)
    : topic === 'jobs' ? jobsNumericAggregates(compatible) : [];
  const categorical = categoricalAggregates(groups);
  const method = numeric.length > 0
    ? 'topic-compatible-envelope'
    : categorical.length > 0 ? 'topic-compatible-frequency' : null;
  return {
    topic,
    compatibleRecordCount: new Set(compatible.map((match) => match.record.projectId)).size,
    method,
    numeric,
    categorical,
  };
}

export function evidenceBasisLabel(basis: EconomicEvidenceBasis): string {
  return basis.replaceAll('-', ' ');
}
